import { useCallback, useEffect, useState } from "react";

/** 테마는 세 상태다 — 시스템 설정을 따름(기본) · 밝게 · 어둡게.
 *
 *  공통 v3 의 tokens.css 는 `<html data-theme>` **두 값**(`dark` · `light`)만 그린다 — 속성이 없으면
 *  다크다. 그래서 "system" 은 속성을 지우는 것이 아니라 **지금 OS 설정을 읽어 둘 중 하나를 쓰는 것**이고,
 *  OS 설정이 바뀌면 다시 쓴다. 예전 자체 토큰은 CSS `@media (prefers-color-scheme)` 가 그 일을 했는데
 *  v3 는 그 블록이 없다(사람 결정 2026-10-09 — 테마 전환은 속성 하나로만). 속성을 지우기만 하면
 *  밝은 OS 를 쓰는 사람도 늘 다크로 열린다. */
export type Theme = "system" | "light" | "dark";

const KEY = "b3x-in.theme";
const LIGHT_QUERY = "(prefers-color-scheme: light)";

function systemTheme(): "light" | "dark" {
  return window.matchMedia?.(LIGHT_QUERY).matches ? "light" : "dark";
}

function apply(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme === "system" ? systemTheme() : theme);
}

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // 사생활 보호 모드 등에서 던진다. 읽기에 실패해도 화면은 떠야 하므로 기본값으로 간다.
  }
  return "system";
}

/** 첫 그림 전에 한 번 부른다(main.tsx) — 훅이 뜨기 전 한 프레임을 다크로 그렸다가 밝게 바뀌는
 *  깜빡임을 막는다. */
export function applyStoredTheme() {
  apply(read());
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => {
    apply(theme);
    if (theme !== "system") return;
    // "시스템" 을 고른 동안에는 OS 설정이 바뀌면 화면도 따라간다. 직접 고른 테마면 들을 이유가 없다.
    const mq = window.matchMedia?.(LIGHT_QUERY);
    if (!mq) return;
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const set = useCallback((next: Theme) => {
    setTheme(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // 저장만 실패한 것이라 이번 방문에는 적용된다. 조용히 넘어간다.
    }
  }, []);

  return [theme, set];
}
